/** Index and selection arithmetic shared by the reorderable lists. */

export type DropPosition = 'before' | 'after' | 'inside';

/**
 * Target index for "remove at `from`, then insert" when an entry is dropped
 * before/after the entry currently at `over` in the same list.
 */
export function reorderIndex(from: number, over: number, position: 'before' | 'after'): number {
  const insertion = position === 'before' ? over : over + 1;
  return from < insertion ? insertion - 1 : insertion;
}

/** Which third (or half, when `allowInside` is false) of a row the pointer is over. */
export function dropPositionAt(offsetY: number, height: number, allowInside: boolean): DropPosition {
  const t = height > 0 ? offsetY / height : 0;
  if (allowInside) return t < 0.25 ? 'before' : t > 0.75 ? 'after' : 'inside';
  return t < 0.5 ? 'before' : 'after';
}

export interface SelectModifiers {
  /** Ctrl/Cmd: toggle the clicked entry. */
  toggle: boolean;
  /** Shift: select the range from the anchor to the clicked entry. */
  range: boolean;
}

/** New selection after clicking `id` in a list displayed in `order`. */
export function selectWithModifiers(
  order: readonly string[],
  current: readonly string[],
  id: string,
  modifiers: SelectModifiers,
  anchor: string | null,
): string[] {
  if (modifiers.range) {
    const from = order.indexOf(anchor ?? current[current.length - 1] ?? id);
    const to = order.indexOf(id);
    if (from >= 0 && to >= 0) {
      const range = order.slice(Math.min(from, to), Math.max(from, to) + 1);
      return modifiers.toggle ? [...new Set([...current, ...range])] : range;
    }
  }
  if (modifiers.toggle) return current.includes(id) ? current.filter((c) => c !== id) : [...current, id];
  return [id];
}

/** Neighbour to focus when moving with the arrow keys; clamps at the ends. */
export function neighbour(order: readonly string[], id: string | null, step: 1 | -1): string | null {
  if (order.length === 0) return null;
  const index = id === null ? -1 : order.indexOf(id);
  if (index < 0) return step === 1 ? order[0]! : order[order.length - 1]!;
  return order[Math.min(order.length - 1, Math.max(0, index + step))]!;
}
